// One shared decision for "what do we show, and what do we report to Sentry"
// when a call can be refused because the live server is behind this account's
// `_users` doc version (#4283). Several admin and coach screens used to repeat
// this by hand, and a fourth hand-written copy is how the decision gets
// inverted by accident. See design-docs/users-doc-versioning.md for the scheme.

import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { isVersionMismatchCouchLoginError } from 'services/couch-login-error-classify';
import { UsersDocTooNewForClientError } from 'util/users-doc-version-guard';
// type-only import, so this small helper never drags the logging service's whole
// dependency chain into the module graph of the screens that use it
import type { LoggingService } from 'services/logging.service';

/**
 * Pick the message to show for a failed call, and report the failure to Sentry
 * only when it deserves that.
 *
 * A version mismatch is an EXPECTED, self-resolving condition — the live server
 * catches up on its next deploy — so it gets its own honest wording and no
 * Sentry report. Every other failure is genuinely unexpected: it is reported,
 * and the caller's own `fallbackMessage` is shown.
 *
 * There are TWO shapes of that condition and this helper must know both, because
 * a screen can hit either. The server's refusal arrives as an HTTP 409 through
 * the cloud-functions wrapper. The client's own guard
 * (`assertUsersDocVersionSupportedOnClient`, used by the officer-only methods
 * that read `_users` straight from the browser) throws a plain Error instead,
 * and matching only the 409 would send that one to Sentry as unexpected — the
 * exact inversion this shared helper exists to prevent.
 *
 * Returns the message the caller should display.
 */
export function describeErrorAndReportIfUnexpected(
  err: unknown,
  logging: LoggingService,
  fallbackMessage: TranslatedString,
): TranslatedString {
  // if this build cannot read the record, say which one and why — the guard's
  // own message names the account and both versions, which beats the generic
  // wording. Plain English is correct here: it can only be raised from the
  // officer-only admin screens, which are untranslated by design.
  if (err instanceof UsersDocTooNewForClientError) {
    return untranslatedString(err.message);
  }
  // if the server is simply behind this account's version, explain that and stay out of Sentry
  if (isVersionMismatchCouchLoginError(err)) {
    return LL.app.serverBehindAccountVersion();
  }
  // Deliberately trackAbnormalException, not trackSilentProblem: this is an
  // unexplained failure and the whole exception (message + stack) is what a
  // debugger needs. It also raises a toast, so a caller that renders the
  // returned message in an alert surfaces it twice — accepted, because losing
  // stack fidelity in Sentry is the worse trade.
  logging.trackAbnormalException(err);
  return fallbackMessage;
}
